/**
 * Everything derived from the sync code, and the encryption of the synced blob. All of it runs on the device.
 *
 *   code ──HKDF──┬─ id    (what the server files the blob under; says nothing about the code)
 *                ├─ auth  (sent with every request; the server keeps only its hash and compares)
 *                └─ key   (AES-256-GCM; the server never has it, so it stores ciphertext it cannot read)
 */
const enc = new TextEncoder();
const dec = new TextDecoder();

export const b64url = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
export const fromB64url = (s: string): Uint8Array<ArrayBuffer> => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));

export interface SyncIdentity {
  id: string; // 32 chars base64url
  auth: string; // 43 chars base64url
  key: CryptoKey;
}

async function hkdf(code: string, info: string, bytes: number): Promise<Uint8Array<ArrayBuffer>> {
  const ikm = await crypto.subtle.importKey("raw", enc.encode(code), "HKDF", false, ["deriveBits"]);
  return new Uint8Array(await crypto.subtle.deriveBits({ name: "HKDF", hash: "SHA-256", salt: enc.encode("pass-the-verse/sync/v1"), info: enc.encode(info) }, ikm, bytes * 8));
}

export async function deriveIdentity(code: string): Promise<SyncIdentity> {
  const [id, auth, raw] = await Promise.all([hkdf(code, "id", 24), hkdf(code, "auth", 32), hkdf(code, "key", 32)]);
  const key = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  return { id: b64url(id), auth: b64url(auth), key };
}

/** JSON → base64url(iv ‖ ciphertext). A fresh random nonce every time. */
export async function encryptPayload(key: CryptoKey, payload: unknown): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(JSON.stringify(payload))));
  const out = new Uint8Array(iv.length + ct.length);
  out.set(iv);
  out.set(ct, iv.length);
  return b64url(out);
}

/** Throws if the blob was not made with this key (a wrong code) or was tampered with. */
export async function decryptPayload<T = unknown>(key: CryptoKey, body: string): Promise<T> {
  const bytes = fromB64url(body);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes.subarray(0, 12) }, key, bytes.subarray(12));
  return JSON.parse(dec.decode(pt)) as T;
}
